import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  GetIupacNameQuery as GetIupacNameQueryResponse,
  GetIupacNameQueryVariables,
} from 'notebook/structured-tables/molecules/__generated/GetIupacNameQuery';
import {GET_IUPAC_NAME_QUERY} from 'notebook/structured-tables/molecules/ChemistryQueries';
import {
  CustomResolutionQueryType,
  GetIupacNameQuery,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {BaseTypedValueType, CustomEvalResult, TypedValue} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

export async function bulkResolveGetIupacNameToServerResolutionResult(
  queries: Array<GetIupacNameQuery>
): Promise<Array<ServerResolutionResult>> {
  const moleculeIds = queries.map((query) => query.moleculeId);

  const queryResult = await doQueryWithoutCache<GetIupacNameQueryResponse, GetIupacNameQueryVariables>(
    {
      query: GET_IUPAC_NAME_QUERY,
    },
    {variables: {moleculeIds}}
  );

  // Build a map of moleculeId -> iupac name for efficient lookup
  const iupacNameByMoleculeId = new Map<string, string | null>();
  for (const molecule of queryResult.molecules) {
    if (isInaccessible(molecule)) {
      iupacNameByMoleculeId.set(molecule.invalidId, null);
    } else {
      const iupacName = molecule.currentVersion.chemicalStructure.iupacName;
      if (iupacName.__typename === 'StringPropertyNode') {
        iupacNameByMoleculeId.set(molecule.id, iupacName.value);
      } else {
        iupacNameByMoleculeId.set(molecule.id, null);
      }
    }
  }

  // Return results in the same order as the input queries
  return queries.map((query) => {
    const iupacName = iupacNameByMoleculeId.get(query.moleculeId);
    if (iupacName === undefined) {
      // Molecule was not returned by the server (e.g. deleted between query construction and execution)
      return makeCustomResolutionResultFromTypedValue(null, 'Could not retrieve IUPAC name');
    }
    if (iupacName == null) {
      // Molecule exists but has no IUPAC name in PubChem — leave cell empty, no error
      return makeCustomResolutionResultFromTypedValue(null);
    }
    return makeCustomResolutionResultFromTypedValue({
      type: FieldType.TEXT,
      value: iupacName,
    });
  });
}

export function getIupacNameColumnCustomEval(
  typedValue: TypedValue,
  columnDefinition: ColumnDefinition,
  cellDependencies: Array<TypedValue | null>,
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  // The molecule ID comes from the first cell dependency (the Name column containing the bioentity)
  const bioentityTypedValue = cellDependencies[0];
  // If there's no bioentity in the dependency, return empty
  if (bioentityTypedValue == null || bioentityTypedValue.type !== BaseTypedValueType.BIOENTITY) {
    return {val: typedValue, error: null, locked: false};
  }
  const moleculeId = bioentityTypedValue.bioentitySummary.id;
  const {resolvedValue, error} = resolveFromResolutionQuery(
    typedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.STOICHIOMETRY_TABLE_GET_IUPAC_NAME,
      moleculeId,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: false};
}
